/**
 * Purpose: "You may also like" cross-sell section on the product detail page.
 * Inputs:  product (Product) - the currently viewed product; used to derive
 *          SKU line prefix and exclude it from results.
 * Outputs: A heading + 4-card responsive grid, or null when no related found.
 * Constraints: Fetches all products once (cached via TanStack Query); never
 *              shows the current product; fills to 4 via type->active fallback.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Product } from '@/lib/types'
import { STALE } from '@/lib/query-client'
import { ProductCard } from './ProductCard'

// ─── SKU-line parsing ─────────────────────────────────────────────────────────

/**
 * Extract the product line prefix from a SKU.
 *
 * SKU patterns:
 *   VET-CAP-VET-ARM-001 -> VET-CAP-VET-ARM
 *   VET-CAP-FNY-NAV-001 -> VET-CAP-FNY-NAV
 *   VET-CAP-GWOT-001    -> VET-CAP-GWOT
 *   VET-CAP-ARM-002     -> VET-CAP-ARM
 *   VET-TEE-001         -> VET-TEE
 *
 * This keeps recommendations on the same design family before falling back to
 * same product_type.
 */
export function parseSkuLinePrefix(sku: string): string | null {
  const parts = sku.toUpperCase().split('-')
  if (parts.length < 3) return null
  if (parts[0] !== 'VET') return null

  if (parts[1] === 'CAP') {
    if ((parts[2] === 'VET' || parts[2] === 'FNY') && parts[3]) {
      return parts.slice(0, 4).join('-')
    }
    if (parts[2] === 'GWOT' || parts[2] === 'ARM') {
      return parts.slice(0, 3).join('-')
    }
  }

  return parts.slice(0, 2).join('-')
}

// ─── Fetcher ──────────────────────────────────────────────────────────────────

async function fetchAllProducts(): Promise<Product[]> {
  const res = await fetch('/api/products')
  if (!res.ok) throw new Error('Failed to load products')
  const json = await res.json() as { products: Product[] }
  return json.products ?? []
}

// ─── Main component ───────────────────────────────────────────────────────────

interface RelatedProductsProps {
  product: Product
}

export function pickRelatedProducts(
  product: Product,
  allProducts: Product[],
  limit = 4,
): Product[] {
  const currentPrefix = parseSkuLinePrefix(product.sku)
  const others = allProducts.filter(
    (p) => p.id !== product.id && p.status === 'active',
  )

  const sameLine = currentPrefix
    ? others.filter((p) => parseSkuLinePrefix(p.sku) === currentPrefix)
    : []

  const pickedIds = new Set(sameLine.map((p) => p.id))
  const sameType = others.filter(
    (p) => !pickedIds.has(p.id) && p.product_type === product.product_type,
  )

  for (const p of sameType) pickedIds.add(p.id)
  const anyActive = others.filter((p) => !pickedIds.has(p.id))

  return [...sameLine, ...sameType, ...anyActive].slice(0, limit)
}

export function RelatedProducts({ product }: RelatedProductsProps) {
  const { data: allProducts = [] } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: fetchAllProducts,
    staleTime: STALE.PRODUCTS,
  })

  const related = useMemo<Product[]>(() => {
    return pickRelatedProducts(product, allProducts, 4)
  }, [allProducts, product])

  if (related.length === 0) return null

  return (
    <section aria-label="You may also like">
      <h2 className="text-2xl font-bold tracking-tight mb-6">
        You may also like
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {related.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  )
}
